import { type FormEvent, useEffect, useRef, useState } from "react";

const APP_URL = "https://portal.aboutnpromote.com";
const DEMO_EMAIL = "support@aboutnpromote.com";

const Arrow = () => <span aria-hidden="true">↗</span>;

function Brand() {
  return (
    <a className="brand" href="#top" aria-label="AboutNPromote home">
      <span className="brand-mark" aria-hidden="true"><i /><i /></span>
      <span>About<b>N</b>Promote</span>
    </a>
  );
}

const heroOpportunities = [
  {
    label: "Branded signage",
    title: ["Your menus, offers,", "events, and story."],
    copy: "Publish your own content across the Windows PCs and TVs your business already uses.",
    action: "Create and publish",
    art: "coffee"
  },
  {
    label: "Permission-based",
    title: ["Nothing plays", "without your approval."],
    copy: "Review each business, creative revision, screen assignment, schedule, and share of screen time.",
    action: "Approve the campaign",
    art: "welcome"
  },
  {
    label: "Local distribution",
    title: ["Reach customers", "inside trusted businesses."],
    copy: "Send an approved promotion to participating screens where local customers already spend time.",
    action: "Schedule the placement",
    art: "offer"
  },
  {
    label: "Operational visibility",
    title: ["Know what played", "and which screens are healthy."],
    copy: "Track completed plays, connected screens, partner businesses, and privacy-preserving QR scans.",
    action: "Review the results",
    art: "event"
  }
] as const;

function OpportunityArt({ art }: { art: (typeof heroOpportunities)[number]["art"] }) {
  if (art === "coffee") {
    return (
      <div className="display-art coffee-art" aria-hidden="true">
        <span className="sun" />
        <span className="cup"><i /></span>
        <span className="steam one" />
        <span className="steam two" />
      </div>
    );
  }

  if (art === "welcome") {
    return (
      <div className="display-art opportunity-art art-welcome" aria-hidden="true">
        <span className="welcome-sun" />
        <span className="welcome-card"><b>Welcome</b><small>Harbor House</small></span>
        <span className="welcome-chip chip-wifi"><i /> Wi-Fi</span>
        <span className="welcome-chip chip-wayfinding"><i /> Floor 2</span>
      </div>
    );
  }

  if (art === "offer") {
    return (
      <div className="display-art opportunity-art art-offer" aria-hidden="true">
        <span className="offer-orbit" />
        <span className="offer-card"><small>Today only</small><b>Lunch<br />special</b><em>12–2 PM</em></span>
        <span className="offer-badge">New</span>
      </div>
    );
  }

  return (
    <div className="display-art opportunity-art art-event" aria-hidden="true">
      <span className="event-orbit" />
      <span className="event-date"><small>SEP</small><b>18</b></span>
      <span className="event-card"><small>Community night</small><b>Meet the makers</b><em>6:30 PM · Main Hall</em></span>
      <span className="event-dot dot-one" />
      <span className="event-dot dot-two" />
    </div>
  );
}

function DisplayPreview() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const slide = heroOpportunities[activeSlide] ?? heroOpportunities[0];

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const interval = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroOpportunities.length);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [paused]);

  const moveSlide = (direction: -1 | 1) => {
    setActiveSlide((current) => (current + direction + heroOpportunities.length) % heroOpportunities.length);
  };

  return (
    <div
      className="display-preview"
      role="region"
      aria-roledescription="carousel"
      aria-label="Ways to use AboutNPromote"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="display-bezel">
        <div className="display-toolbar">
          <span><i /> AboutNPromote</span>
          <small>Connected</small>
        </div>
        <div className="display-slide" key={slide.art} role="group" aria-roledescription="slide" aria-label={`${activeSlide + 1} of ${heroOpportunities.length}: ${slide.label}`}>
          <div className="display-copy">
            <span className="display-label">{slide.label}</span>
            <h3>{slide.title[0]}<br />{slide.title[1]}</h3>
            <p>{slide.copy}</p>
            <b>{slide.action} <Arrow /></b>
          </div>
          <OpportunityArt art={slide.art} />
        </div>
        <button className="carousel-arrow carousel-previous" type="button" aria-label="Previous opportunity" onClick={() => moveSlide(-1)}>←</button>
        <div className="display-dots" aria-label="Choose an opportunity">
          {heroOpportunities.map((opportunity, index) => (
            <button
              className={index === activeSlide ? "active" : ""}
              type="button"
              key={opportunity.label}
              aria-label={`Show ${opportunity.label.toLowerCase()}`}
              aria-current={index === activeSlide ? "true" : undefined}
              onClick={() => setActiveSlide(index)}
            />
          ))}
        </div>
        <button className="carousel-arrow carousel-next" type="button" aria-label="Next opportunity" onClick={() => moveSlide(1)}>→</button>
      </div>
      <div className="display-stand" aria-hidden="true" />
      <div className="preview-note note-left"><strong>Four opportunities</strong><span>Tap to explore each one</span></div>
      <div className="preview-note note-right"><strong>Offline ready</strong><span>Content keeps playing</span></div>
    </div>
  );
}

function WorkspacePreview() {
  return (
    <div className="workspace-preview" aria-label="AboutNPromote dashboard showing connected screens and scheduled content">
      <div className="workspace-sidebar">
        <Brand />
        <span className="side-active"><i /> Overview</span>
        <span><i /> Content</span>
        <span><i /> Displays</span>
        <span><i /> Promotions</span>
      </div>
      <div className="workspace-main">
        <div className="workspace-heading"><div><small>Screen network</small><strong>Everything on brand</strong></div><button type="button">+ New playlist</button></div>
        <div className="workspace-stats">
          <article><span>Connected screens</span><strong>05</strong><small>All healthy</small></article>
          <article><span>Scheduled items</span><strong>12</strong><small>Across 2 locations</small></article>
          <article><span>Promotions to review</span><strong>03</strong><small>Approval required</small></article>
        </div>
        <div className="workspace-list">
          <div className="workspace-list-head"><strong>Display devices</strong><span>View all</span></div>
          <div><i className="device-thumb orange" /><span><strong>Main entrance</strong><small>Welcome &amp; offers playlist</small></span><b>Online</b></div>
          <div><i className="device-thumb green" /><span><strong>Service counter</strong><small>Menu &amp; announcements</small></span><b>Online</b></div>
          <div><i className="device-thumb gold" /><span><strong>Waiting area</strong><small>Partner promotion pending</small></span><b>Review</b></div>
        </div>
      </div>
    </div>
  );
}

function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (prefersReducedMotion.matches) {
      videoRef.current?.pause();
      setPlaying(false);
    }
  }, []);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextMuted = !muted;
    video.muted = nextMuted;
    setMuted(nextMuted);
    if (!nextMuted && video.paused) {
      void video.play().catch(() => setPlaying(false));
    }
  };

  return (
    <div className="hero-video-frame">
      <video
        ref={videoRef}
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="metadata"
        poster="/aboutnpromote-story-poster.jpg"
        aria-describedby="hero-video-description"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      >
        <source src="/aboutnpromote-story.mp4" type="video/mp4" />
        <track default kind="captions" src="/aboutnpromote-story.vtt" srcLang="en" label="English" />
      </video>
      <p className="sr-only" id="hero-video-description">A narrated one-minute overview of AboutNPromote: manage screen content, connect with trusted businesses, approve promotions, schedule playback, and measure delivery.</p>
      <div className="video-controls">
        <button className="video-toggle" type="button" onClick={togglePlayback} aria-label={`${playing ? "Pause" : "Play"} the one-minute AboutNPromote overview`}>
          <span aria-hidden="true">{playing ? "Ⅱ" : "▶"}</span>{playing ? "Pause" : "Play"}<small>1:00</small>
        </button>
        <button className="video-toggle" type="button" onClick={toggleSound} aria-label={`Turn video sound ${muted ? "on" : "off"}`}>
          <span aria-hidden="true">{muted ? "◖" : "◕"}</span>Sound<small>{muted ? "Off" : "On"}</small>
        </button>
      </div>
    </div>
  );
}

const workflow = [
  { number: "01", title: "Create", copy: "Build branded menus, announcements, events, specials, and promotions." },
  { number: "02", title: "Approve", copy: "Review every partner, creative revision, destination, and placement." },
  { number: "03", title: "Schedule", copy: "Choose screens, dates, time windows, and the allowed share of playback." },
  { number: "04", title: "Display", copy: "Publish to connected Windows screens with offline-ready playback." },
  { number: "05", title: "Measure", copy: "Report completed plays, unique screens, partners, and QR scans." }
];

const howItWorks = [
  { number: "01", title: "Connect your screen", copy: "Install the AboutNPromote Windows player and assign the display to a location." },
  { number: "02", title: "Publish your content", copy: "Create playlists for your menus, offers, announcements, events, and brand media." },
  { number: "03", title: "Choose promotion preferences", copy: "Select trusted businesses and categories, block competitors, and approve each campaign." },
  { number: "04", title: "Reach local customers", copy: "Your approved promotion can run on participating screens while each host keeps control." }
];

const audiences = [
  { title: "Independent business owners", copy: "Keep your in-store screens useful, branded, and connected to businesses you trust." },
  { title: "Multi-location operators", copy: "Coordinate content, schedules, screen health, and local promotions from one dashboard." },
  { title: "Local advertisers", copy: "Place approved promotions on participating screens and report completed delivery and QR scans." },
  { title: "Screen hosts", copy: "Use your own content first, approve every partner promotion, and control the schedule." },
  { title: "Community organizations", copy: "Share relevant events and resources through participating local venues." }
];

function DemoForm() {
  const [prepared, setPrepared] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = `Demo request from ${String(data.get("businessName") ?? "a local business")}`;
    const body = [
      `Name: ${String(data.get("name") ?? "")}`,
      `Business email: ${String(data.get("email") ?? "")}`,
      `Business name: ${String(data.get("businessName") ?? "")}`,
      `Number of locations: ${String(data.get("locations") ?? "")}`,
      `Existing screens: ${String(data.get("screens") ?? "")}`
    ].join("\n");
    setPrepared(true);
    window.location.href = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <form className="demo-form" onSubmit={submit}>
      <div className="form-grid">
        <label>Name<input name="name" autoComplete="name" required /></label>
        <label>Business email<input name="email" type="email" autoComplete="email" required /></label>
        <label>Business name<input name="businessName" autoComplete="organization" required /></label>
        <label>Number of locations<input name="locations" type="number" min="1" inputMode="numeric" required /></label>
        <label className="form-wide">Do you already have screens?<select name="screens" defaultValue="" required><option value="" disabled>Select one</option><option value="Yes">Yes</option><option value="No">No</option><option value="Planning now">Planning now</option></select></label>
      </div>
      <button className="button" type="submit">Request a Demo <Arrow /></button>
      <p className="form-note">This prepares an email to {DEMO_EMAIL}; no account creation is required.</p>
      <p className="form-status" aria-live="polite">{prepared ? "Your email application should open with the request details filled in." : ""}</p>
    </form>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <header className="site-header" id="top">
        <div className="shell header-inner">
          <Brand />
          <button className="menu-button" type="button" aria-expanded={menuOpen} aria-controls="site-navigation" onClick={() => setMenuOpen((open) => !open)}>
            <span className="sr-only">Toggle navigation</span><i /><i />
          </button>
          <nav className={menuOpen ? "open" : ""} id="site-navigation" aria-label="Main navigation">
            <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="#solutions" onClick={() => setMenuOpen(false)}>Solutions</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
            <a className="nav-sign-in" href={APP_URL}>Sign in</a>
            <a className="button button-small" href="#request-demo" onClick={() => setMenuOpen(false)}>Request a Demo <Arrow /></a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-orbit orbit-one" aria-hidden="true" />
          <div className="hero-orbit orbit-two" aria-hidden="true" />
          <div className="shell hero-grid">
            <div className="hero-copy hero-copy-top">
              <h1>Your business. On your screens—and theirs.</h1>
            </div>
            <div className="hero-stage">
              <div className="hero-context hero-context-left"><span>Digital signage</span><strong>Your content stays first.</strong><p>Use the Windows PCs and TVs you already own.</p></div>
              <div className="hero-product"><HeroVideo /></div>
              <div className="hero-context hero-context-right"><span>Permission-based</span><strong>You approve every placement.</strong><p>Choose partners, block competitors, and stop anytime.</p></div>
            </div>
            <div className="hero-bottom">
              <p>Manage branded content, trusted connections, and measurable promotion from one dashboard.</p>
              <div className="hero-actions">
                <a className="button" href="#request-demo">Request a Demo <Arrow /></a>
                <a className="text-link" href="#how-it-works">See how it works <span aria-hidden="true">↓</span></a>
              </div>
            </div>
          </div>
        </section>

        <section className="place-strip" aria-label="Designed for customer-facing local businesses">
          <div className="shell"><span>Built for customer-facing spaces</span><div><b>Restaurants</b><i /> <b>Retail</b><i /> <b>Salons</b><i /> <b>Clinics</b><i /> <b>Gyms</b><i /> <b>Community venues</b></div></div>
        </section>

        <section className="opportunities-section section" aria-labelledby="opportunities-title">
          <div className="shell opportunities-grid">
            <div className="section-copy"><div className="eyebrow"><i /> Content for every moment</div><h2 id="opportunities-title">Your space has a story. Put it on screen.</h2><p>Move from branded content to approved local promotion without changing hardware or giving up control.</p><a className="text-link dark" href="#product">Explore the platform <Arrow /></a></div>
            <DisplayPreview />
          </div>
        </section>

        <section className="product-loop-section section" id="product">
          <div className="shell">
            <div className="section-heading centered"><div className="eyebrow"><i /> The complete product loop</div><h2>From content creation to confirmed playback.</h2><p>See exactly what the platform does: create, approve, schedule, display, and measure from one operational workspace.</p></div>
            <ol className="workflow-strip" aria-label="AboutNPromote product workflow">
              {workflow.map((step) => <li key={step.number}><span>{step.number}</span><strong>{step.title}</strong><p>{step.copy}</p></li>)}
            </ol>
            <div className="product-showcase">
              <WorkspacePreview />
              <div className="showcase-copy">
                <div className="eyebrow"><i /> A real operating platform</div>
                <h3>Put every screen on brand—and keep the network healthy.</h3>
                <p>Publish content, coordinate locations, assign playlists, review incoming promotions, and monitor connected screen health without switching tools.</p>
                <ul><li>Content and playlist management</li><li>Location and display assignment</li><li>Promotion review and scheduling</li><li>Completed-play and QR-scan reporting</li></ul>
              </div>
            </div>
          </div>
        </section>

        <section className="solutions-section section" id="solutions">
          <div className="shell">
            <div className="section-heading centered"><div className="eyebrow"><i /> One platform. Two clear benefits.</div><h2>Manage your own signage. Grow through trusted screens.</h2><p>Your own branded content and permission-based cross-promotion work together—but you stay in control of both.</p></div>
            <div className="solution-grid">
              <article className="solution-card signage-solution"><span className="solution-number">01</span><div className="eyebrow"><i /> Digital signage management</div><h3>Keep every customer-facing screen useful and on brand.</h3><p>Control the content inside your own business from one dashboard.</p><ul><li>Menus and service information</li><li>Announcements and events</li><li>Specials and branded promotions</li><li>Playlists, schedules, and locations</li><li>Connected-screen health</li></ul></article>
              <article className="solution-card promotion-solution"><span className="solution-number">02</span><div className="eyebrow light"><i /> Local cross-promotion</div><h3>Reach customers through businesses they already trust.</h3><p>Connect with participating businesses and distribute approved promotions across the local network.</p><ul><li>Invite and approve trusted partners</li><li>Block competitors and categories</li><li>Review every creative revision</li><li>Choose screens, schedules, and playback share</li><li>Report completed plays and QR scans</li></ul></article>
            </div>
          </div>
        </section>

        <section className="steps-section section" id="how-it-works">
          <div className="shell">
            <div className="section-heading centered"><div className="eyebrow"><i /> How it works</div><h2>Four steps from screen to local reach.</h2><p>No proprietary media player is required. Connect supported Windows devices, publish your own content, then opt into trusted promotion.</p></div>
            <div className="how-grid">
              {howItWorks.map((step) => <article key={step.number}><span>{step.number}</span><h3>{step.title}</h3><p>{step.copy}</p></article>)}
            </div>
          </div>
        </section>

        <section className="network-visual-section" aria-labelledby="network-visual-title">
          <div className="shell">
            <div className="network-visual-heading">
              <div><div className="eyebrow light"><i /> Promotion Circles</div><h2 id="network-visual-title">About your business. Promote your community.</h2></div>
              <p>Your business can appear on trusted participating screens, and you can host relevant promotions—only after reviewing and approving them.</p>
            </div>
            <figure className="network-visual-card">
              <img src="/og.png" alt="AboutNPromote network connecting screens at a coffee shop, retailer, wellness business, and community event" />
            </figure>
          </div>
        </section>

        <section className="audience-section section" id="audiences">
          <div className="shell">
            <div className="section-heading centered"><div className="eyebrow"><i /> Built for the full local ecosystem</div><h2>Choose the path that matches your role.</h2><p>AboutNPromote supports businesses that own screens, businesses that want local reach, and organizations coordinating multiple locations.</p></div>
            <div className="audience-grid">{audiences.map((audience, index) => <article key={audience.title}><span>0{index + 1}</span><h3>{audience.title}</h3><p>{audience.copy}</p></article>)}</div>
          </div>
        </section>

        <section className="control-section section" id="trust">
          <div className="shell">
            <div className="control-heading"><div><div className="eyebrow light"><i /> Permission-based means permission-based</div><h2>You stay in control. Nothing appears unless you approve it.</h2></div><p>Approval is specific to the business, creative revision, destination, screens, schedule, and share of screen time. A change requires review again.</p></div>
            <div className="control-grid">
              <article><strong>Approve every placement</strong><p>Partners can submit promotions, but they cannot publish directly to your screens.</p></article>
              <article><strong>Block competitors and categories</strong><p>Choose who fits your audience and pause or remove a connection at any time.</p></article>
              <article><strong>Your content stays first</strong><p>Partner content is capped at 20% of automatic playback, and you may set a lower limit.</p></article>
              <article id="privacy"><strong>No camera-based tracking</strong><p>The player does not use cameras, microphones, facial recognition, or audience-view claims.</p></article>
              <article id="security"><strong>Protected device access</strong><p>Short-lived pairing, Windows-protected credentials, tenant isolation, and audited approvals protect the network.</p></article>
              <article id="supported-devices"><strong>Supported screens</strong><p>Use a supported Windows PC connected to a TV or display. Touch is optional; timed playback works without it.</p></article>
            </div>
            <div className="company-proof" id="about"><div><span className="company-kicker">Company</span><strong>Operated by Niyamite LLC</strong><p>AboutNPromote is an early-stage product built for independent businesses and customer-facing screen networks.</p></div><div><a href={`mailto:${DEMO_EMAIL}`}>Business contact: {DEMO_EMAIL}</a><a href={`${APP_URL}/status`}>Service status</a><a href="/content-policy.html">Promotion content policy</a></div></div>
          </div>
        </section>

        <section className="pricing-section section" id="pricing">
          <div className="shell">
            <div className="section-heading centered"><div className="eyebrow"><i /> Founding Business Program</div><h2>Help shape the network from the beginning.</h2><p>Join the first group of participating businesses, connect your existing screens, and work directly with the team as the network grows.</p></div>
            <div className="pricing-grid pricing-single">
              <article className="featured"><span className="popular">Early access</span><h3>Founding Business</h3><p className="price"><strong>$79.99</strong><span>/month</span></p><p>A straightforward starting plan for branded signage and permission-based local promotion.</p><div className="plan-line" /><b>Up to 5 connected screens</b><ul><li>Content, playlist, and location management</li><li>Partner invitations and campaign approval</li><li>Completed-play and QR-scan reporting</li><li>Offline-ready Windows playback</li></ul><a className="button" href="#request-demo">Request a Demo <Arrow /></a></article>
              <article className="pricing-story"><div className="eyebrow"><i /> Bring your own device</div><h3>Keep your hardware. Upgrade what your screens can do.</h3><p>No proprietary media player is required. Pair your supported Windows screens, manage them centrally, and keep verified media ready for interrupted connectivity.</p><div className="plan-line" /><b>Direct onboarding and ongoing product support.</b><ul><li>One dashboard for screens and locations</li><li>Your own branded content stays the priority</li><li>Partner content requires explicit approval</li><li>Screen health and delivery reporting</li></ul><a className="text-link dark" href="#request-demo">Discuss your setup <Arrow /></a></article>
            </div>
          </div>
        </section>

        <section className="faq-section section">
          <div className="shell faq-grid"><div className="section-copy"><div className="eyebrow"><i /> Clear answers before installation</div><h2>Know exactly what goes on your screens.</h2><p>Need an answer specific to your setup?</p><a href={`mailto:${DEMO_EMAIL}`} className="text-link dark">Contact the team <Arrow /></a></div><div className="faq-list">
            <details><summary>Is AboutNPromote digital signage software or an advertising network?<span>+</span></summary><p>Both, in two distinct layers. First, you manage your own branded signage. If you choose, you can also join a permission-based network for approved cross-promotion.</p></details>
            <details><summary>Do you provide the screens?<span>+</span></summary><p>No proprietary display hardware is required. Use a supported Windows PC connected to the TV or display your business already owns.</p></details>
            <details><summary>Can another business publish directly to my screens?<span>+</span></summary><p>No. You approve the partner, creative revision, destination, screens, schedule, and playback allocation before a promotion can run.</p></details>
            <details><summary>Can I block competitors or unsuitable content?<span>+</span></summary><p>Yes. Block businesses and categories, reject a campaign, pause an active placement, and report prohibited content under the published content policy.</p></details>
            <details><summary>What does campaign reporting measure?<span>+</span></summary><p>Reports show completed automatic plays, unique screens, participating businesses, and privacy-preserving QR scans. Plays prove device delivery—not human attention or impressions.</p></details>
            <details><summary>What data is collected from screens?<span>+</span></summary><p>The service records operational screen health, content synchronization, completed partner playback, and QR-scan events needed to operate and report the service. The player does not use cameras or facial recognition.</p></details>
            <details><summary>What happens if the internet goes down?<span>+</span></summary><p>Verified media stays in the local cache so assigned content can continue playing. The player reconnects and checks for updates when service returns.</p></details>
          </div></div>
        </section>

        <section className="demo-section" id="request-demo">
          <div className="shell demo-grid">
            <div className="demo-copy"><div className="eyebrow light"><i /> Request a Demo</div><h2>See how the complete loop fits your business.</h2><p>Tell us about your locations and existing screens. We’ll focus the conversation on your signage, approval preferences, and local-promotion goals.</p><div className="demo-assurances"><span>✓ No account creation</span><span>✓ Five short fields</span><span>✓ Talk directly with the product team</span></div></div>
            <DemoForm />
          </div>
        </section>
      </main>

      <footer><div className="shell footer-grid expanded-footer"><div><Brand /><p><strong>About your business. Promote your community.</strong></p><p>AboutNPromote is operated by Niyamite LLC.</p></div><div><strong>Product</strong><a href="#product">Product</a><a href="#solutions">Solutions</a><a href="#how-it-works">How it works</a><a href="#pricing">Pricing</a></div><div><strong>Trust</strong><a href="#privacy">Privacy &amp; data</a><a href="#security">Security</a><a href="#supported-devices">Supported devices</a><a href="/content-policy.html">Content policy</a></div><div><strong>Company</strong><a href="#about">About</a><a href={`mailto:${DEMO_EMAIL}`}>Contact</a><a href={`${APP_URL}/status`}>Service status</a><a href={APP_URL}>Sign in</a></div></div><div className="shell footer-bottom"><span>© {new Date().getFullYear()} Niyamite LLC. AboutNPromote.</span><span>Digital signage and permission-based local promotion.</span></div></footer>
    </>
  );
}

export default App;
